import React, { useState, useEffect } from 'react';
import Card from '../common/Card.jsx';
import Pagination from '../common/Pagination.jsx';
import pieceService from '../../services/pieceService.js';
import typeArticleService from '../../services/typeArticleService';
import AddPieceModal from './PiecesRechange/AddPieceModal.jsx';
import EditPieceModal from './PiecesRechange/EditPieceModal.jsx';
import DeletePieceModal from './PiecesRechange/DeletePieceModal.jsx';
import { useTheme } from '../../contexts/ThemeContext';

const PiecesRechange = () => {
  const [pieces, setPieces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    maxPage: 0
  });

  // Filters
  const [filters, setFilters] = useState({
    searchTerms: '',
    familleIds: []
  });
  const [showFilters, setShowFilters] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(false);

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPiece, setSelectedPiece] = useState(null);
  const { isDarkMode } = useTheme();

  // Type articles for famille dropdown
  const [typeArticles, setTypeArticles] = useState([]);
  const [typeArticlesLoading, setTypeArticlesLoading] = useState(false);

  // View mode
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'

  // Load type articles for famille dropdown
  useEffect(() => {
    const fetchTypeArticles = async () => {
      setTypeArticlesLoading(true);
      try {
        const result = await typeArticleService.getTypeArticles(1000, 1, {});
        if (result.success && Array.isArray(result.data.list)) {
          setTypeArticles(result.data.list);
        } else {
          setTypeArticles([]);
        }
      } catch {
        setTypeArticles([]);
      } finally {
        setTypeArticlesLoading(false);
      }
    };
    fetchTypeArticles();
  }, []);

  // Load pieces from API
const loadPieces = async (pageSize = pagination.pageSize, page = pagination.page, filterParams = filters) => {
  setLoading(true);
  setError('');
  try {
    const apiFilters = {};
    if (filterParams.searchTerms?.trim()) apiFilters.searchTerms = filterParams.searchTerms.trim();
    if (filterParams.familleIds && filterParams.familleIds.length > 0) {
      apiFilters.familleId = filterParams.familleIds.join(',');
    }
    const result = await pieceService.getPieces(pageSize, page, apiFilters);

    // --- FIX: handle array or paginated object ---
    let pieces = [];
    let total = 0, maxPage = 1;
    if (Array.isArray(result.data)) {
      pieces = result.data;
      total = result.data.length;
      maxPage = 1;
    } else if (result.data && Array.isArray(result.data.list)) {
      pieces = result.data.list;
      total = Number(result.data.total) || pieces.length;
      maxPage = Number(result.data.maxPage) || 1;
    }

    if (result.success) {
      setPieces(pieces);
      setPagination({
        page: Number(result.data.page) || 1,
        pageSize: Number(result.data.pageSize) || 10,
        total,
        maxPage
      });
    } else {
      setError(result.message);
    }
  } catch (error) {
    setError('Erreur lors du chargement des pièces');
  } finally {
    setLoading(false);
  }
};

useEffect(() => {
  loadPieces();
}, []);

// Full right-content loading indicator while API responds (keeps hooks order)
if (loading) {
  return (
    <div className="p-4 sm:p-6 min-h-screen flex items-start justify-center">
      <div className="w-full max-w-6xl">
        <div className={`rounded-2xl shadow-sm border p-8 flex flex-col items-center justify-center ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} style={{ minHeight: '60vh' }}>
          <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${isDarkMode ? 'border-primary-400' : 'border-primary-600'}`} />
          <div className={`mt-4 text-lg font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Chargement des pièces...</div>
          <div className={`mt-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Veuillez patienter pendant la récupération des données.</div>
        </div>
      </div>
    </div>
  );
}

  // Filter handlers
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFamilleChange = (e) => {
    const options = Array.from(e.target.selectedOptions);
    handleFilterChange('familleIds', options.map(opt => opt.value));
  };

  const applyFilters = () => {
    setFiltersApplied(
      filters.searchTerms.trim() !== '' ||
      (filters.familleIds && filters.familleIds.length > 0)
    );
    loadPieces(pagination.pageSize, 1, filters);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({ searchTerms: '', familleIds: [] });
    setFiltersApplied(false);
    loadPieces(pagination.pageSize, 1, { searchTerms: '', familleIds: [] });
    setShowFilters(false);
  };

  // Modal handlers
  const handleAdd = () => setShowAddModal(true);
  const handleEdit = (piece) => {
    setSelectedPiece(piece);
    setShowEditModal(true);
  };
  const handleDelete = (piece) => {
    setSelectedPiece(piece);
    setShowDeleteModal(true);
  };

  // Pagination handlers
  const handlePageChange = (newPage) => {
    loadPieces(pagination.pageSize, newPage, filters);
  };
  const handlePageSizeChange = (newPageSize) => {
    loadPieces(newPageSize, 1, filters);
  };

  return (
    <div className="space-y-4 sm:space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 truncate">
            Pièces de rechange et service
          </h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">
            Gestion des pièces de rechange et services
            {filtersApplied && (
              <span className="text-primary-600 font-medium"> (Filtré)</span>
            )}
          </p>
        </div>
        <div className="flex space-x-3 flex-shrink-0">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`border px-4 py-2 rounded-lg transition-colors text-sm font-medium ${
              filtersApplied
                ? 'bg-primary-50 border-primary-300 text-primary-700'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <i className="fas fa-filter mr-2"></i>
            Filtrer
            {filtersApplied && <span className="ml-1 bg-primary-500 text-white rounded-full px-1.5 py-0.5 text-xs">•</span>}
          </button>
          <button
            onClick={handleAdd}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-all text-sm font-medium"
          >
            <i className="fas fa-plus mr-2"></i>
            Nouvelle Pièce / Service
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 sm:px-6 sm:py-4 rounded-lg">
          <div className="flex items-start">
            <i className="fas fa-exclamation-circle mr-2 mt-0.5 flex-shrink-0"></i>
            <span className="text-sm sm:text-base">{error}</span>
          </div>
        </div>
      )}

      {/* Filters Panel */}
      {showFilters && (
        <Card className={`border-l-4 border-l-primary-500 ${isDarkMode ? 'bg-gray-800 border-gray-700' : ''}`}>
          <div className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
              <div className="flex items-center mb-2 sm:mb-0">
                <div className={`p-2 rounded-lg mr-3 ${isDarkMode ? 'bg-gray-700' : 'bg-primary-100'}`}>
                  <i className={`fas fa-filter ${isDarkMode ? 'text-primary-400' : 'text-primary-600'}`}></i>
                </div>
                <div>
                  <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Filtres de recherche</h3>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Affinez votre recherche de pièces/services</p>
                </div>
              </div>
              <button
                onClick={() => setShowFilters(false)}
                className={`self-end sm:self-auto rounded-full p-1 transition-colors ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-400 hover:text-gray-600'}`}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <i className="fas fa-search mr-2 text-gray-400"></i>
                  Recherche par désignation
                </label>
                <input
                  type="text"
                  value={filters.searchTerms}
                  onChange={e => handleFilterChange('searchTerms', e.target.value)}
                  placeholder="Désignation..."
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors
                    ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'}`}
                />
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
              <button
                onClick={clearFilters}
                className={`px-4 py-2 rounded-lg transition-colors text-sm font-medium order-2 sm:order-1
                  ${isDarkMode ? 'text-gray-200 bg-gray-700 hover:bg-gray-600' : 'text-gray-600 bg-gray-100 hover:bg-gray-200'}`}
              >
                <i className="fas fa-times mr-2"></i>
                Effacer
              </button>
              <button
                onClick={applyFilters}
                className={`px-6 py-2 rounded-lg transition-colors text-sm font-medium order-1 sm:order-2
                  ${isDarkMode ? 'bg-primary-700 text-white hover:bg-primary-800' : 'bg-primary-600 text-white hover:bg-primary-700'}`}
              >
                <i className="fas fa-search mr-2"></i>
                Appliquer les filtres
              </button>
            </div>
          </div>
        </Card>
      )}

      {/* View Mode Selector */}
      <Card className={isDarkMode ? 'bg-gray-800 border-gray-700' : ''}>
        <div className="p-4 flex items-center justify-between">
          <div className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {pagination.total} pièce{pagination.total > 1 ? 's' : ''} au total
            {pagination.total > pagination.pageSize && (
              <span className="ml-1 hidden sm:inline">
                • Page {pagination.page} sur {pagination.maxPage}
              </span>
            )}
          </div>
          <div className={`flex rounded-lg p-1 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <button
              onClick={() => setViewMode('cards')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'cards'
                  ? isDarkMode
                    ? 'bg-gray-900 text-primary-400 shadow-sm'
                    : 'bg-white text-primary-600 shadow-sm'
                  : isDarkMode
                  ? 'text-gray-300 hover:text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="fas fa-th-large mr-1.5"></i>
              <span className="hidden sm:inline">Cartes</span>
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'table'
                  ? isDarkMode
                    ? 'bg-gray-900 text-primary-400 shadow-sm'
                    : 'bg-white text-primary-600 shadow-sm'
                  : isDarkMode
                  ? 'text-gray-300 hover:text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="fas fa-list mr-1.5"></i>
              <span className="hidden sm:inline">Liste</span>
            </button>
          </div>
        </div>
      </Card>

      {/* Cards view */}
      {viewMode === 'cards' && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
            {pieces.map(piece => (
              <Card key={piece.id} className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : ''} hover:shadow-lg transition-shadow duration-200`}>
                <div className="p-4 sm:p-6 flex flex-col h-full">
                  <div className="flex items-center mb-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mr-3 ${isDarkMode ? 'bg-gray-700' : 'bg-primary-100'}`}>
                      <i className={`fas fa-cogs text-lg ${isDarkMode ? 'text-gray-200' : 'text-primary-600'}`}></i>
                    </div>
                    <div>
                      <h3 className={`font-semibold text-base break-words line-clamp-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {piece.designation}
                      </h3>
                      <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>ID: {piece.id}</p>
                    </div>
                  </div>
                  <div className="mb-4 flex-1">
                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      <i className="fas fa-tags mr-1"></i>
                      {piece.famille && Array.isArray(piece.famille)
                        ? piece.famille.map(f => f.designation).join(', ')
                        : piece.famille?.designation || piece.famille || ''}
                    </div>
                  </div>
                  <div className="mb-4 flex-1">
                    <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      <i className="fas fa-layer-group mr-1"></i>
                      {piece.typeArticleId
                        ? piece.typeArticleId
                            .split(',')
                            .map(id => typeArticles.find(t => String(t.id) === id)?.designation || id)
                            .join(', ')
                        : ''}
                    </div>
                  </div>
                  <div className="flex gap-2 mt-auto">
                    <button
                      onClick={() => handleEdit(piece)}
                      className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors
                        ${isDarkMode ? 'bg-amber-900 text-amber-200 hover:bg-amber-800' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'}`}
                      title="Modifier"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      onClick={() => handleDelete(piece)}
                      className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors
                        ${isDarkMode ? 'bg-red-900 text-red-200 hover:bg-red-800' : 'bg-red-50 text-red-700 hover:bg-red-100'}`}
                      title="Supprimer"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
              </Card>
            ))}
            {pieces.length === 0 && (
              <Card className={isDarkMode ? 'bg-gray-800 border-gray-700' : ''}>
                <div className={`p-6 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>Aucune pièce/service trouvée.</div>
              </Card>
            )}
          </div>
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.maxPage}
            pageSize={pagination.pageSize}
            totalItems={pagination.total}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </>
      )}

      {/* Table view */}
      {viewMode === 'table' && (
        <Card className={isDarkMode ? 'bg-gray-800 border-gray-700' : ''}>
          <div className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className={`min-w-full divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                  <tr>
                    <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      ID
                    </th>
                    <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Désignation
                    </th>
                    <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider hidden md:table-cell ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Famille
                    </th>
                    <th className={`px-3 sm:px-6 py-3 text-left text-xs font-medium uppercase tracking-wider hidden lg:table-cell ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Types d'articles
                    </th>
                    <th className={`px-3 sm:px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className={isDarkMode ? 'bg-gray-800 divide-y divide-gray-700' : 'bg-white divide-y divide-gray-200'}>
                  {pieces.map(piece => (
                    <tr key={piece.id} className={isDarkMode ? 'hover:bg-gray-700 transition-colors' : 'hover:bg-gray-50 transition-colors'}>
                      <td className={`px-3 sm:px-6 py-4 text-xs sm:text-sm font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                        {piece.id}
                      </td>
                      <td className="px-3 sm:px-6 py-4 min-w-[120px]">
                        <div className="flex items-center min-w-0">
                          <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center mr-2 sm:mr-3 flex-shrink-0 ${isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-primary-100 text-primary-600'}`}>
                            <i className="fas fa-cogs text-xs sm:text-sm"></i>
                          </div>
                          <div className="min-w-0 flex-1">
                            <div
                              className={`text-xs sm:text-sm font-medium break-words line-clamp-2 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}
                              title={piece.designation}
                            >
                              {piece.designation}
                            </div>
                            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {/* Show famille on mobile */}
                              <span className="md:hidden">{piece.famille || '-'}</span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className={`px-3 sm:px-6 py-4 hidden md:table-cell text-xs sm:text-sm font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                        {piece.famille || '-'}
                      </td>
                      <td className={`px-3 sm:px-6 py-4 hidden lg:table-cell text-xs sm:text-sm ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                        {piece.typeArticleId
                          ? piece.typeArticleId
                              .split(',')
                              .map(id => typeArticles.find(t => String(t.id) === id)?.designation || id)
                              .join(', ')
                          : ''}
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-1 sm:space-x-2">
                          <button
                            onClick={() => handleEdit(piece)}
                            className={`p-1 rounded text-xs sm:text-sm transition-colors
                              ${isDarkMode ? 'text-amber-300 hover:text-amber-500' : 'text-amber-600 hover:text-amber-900'}`}
                            title="Modifier"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                          <button
                            onClick={() => handleDelete(piece)}
                            className={`p-1 rounded text-xs sm:text-sm transition-colors
                              ${isDarkMode ? 'text-red-300 hover:text-red-500' : 'text-red-600 hover:text-red-900'}`}
                            title="Supprimer"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {pieces.length === 0 && (
                    <tr>
                      <td colSpan={5} className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>
                        Aucune pièce/service trouvée.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.maxPage}
            pageSize={pagination.pageSize}
            totalItems={pagination.total}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </Card>
      )}

      {/* Modals */}
      <AddPieceModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => loadPieces()}
        typeArticles={typeArticles}
      />
      <EditPieceModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSuccess={() => loadPieces()}
        typeArticles={typeArticles}
        piece={selectedPiece}
      />
      <DeletePieceModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onSuccess={() => loadPieces()}
        piece={selectedPiece}
      />
    </div>
  );
};

export default PiecesRechange;